/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/no-children-prop */
import React, { useEffect } from "react";
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Box,
  useDisclosure,
  Button,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  Text,
  HStack,
  Spinner,
  Tooltip
} from "@chakra-ui/react";
import { Control, Controller, FieldError } from "react-hook-form";
import {
  GeoJSON,
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents
} from "react-leaflet";
import L from "leaflet";
import { BoundRedirector } from "components/Organismos/Map/Controls/BoundRedirector";
import "leaflet/dist/leaflet.css";
import { RiMenu2Fill, RiFullscreenLine } from "react-icons/ri";
import { ModalConsultaPrevia } from "pages/LicenciamentoUrbano/SolicitarRequerimento/components/FormRequerimento/ModalConsultaPrevia";
import { useAppDispatch, useTypedSelector } from "store/hooks";
import { obterPlanoDiretorNoPonto } from "store/modules/consultaprevia/actions/obterPlanoDiretorNoPonto";
import { GeometriaLote } from "store/hooks/useVerificarLoteFiscal";

type Coord = { lat: number; lng: number };

interface Props {
  name: string;
  label?: string;
  isAtributos?: boolean;
  control: Control<any>;
  error?: FieldError;
  // D1 — Lote Fiscal: avisa o formulário que um ponto foi marcado/movido, para
  // disparar a verificação de lote e o pré-preenchimento das seções prefixadas.
  onPontoSelecionado?: (coord: Coord) => void;
  // Geometria (GeoJSON) do lote fiscal identificado, destacada em verde claro.
  geometria?: GeometriaLote;
  // Spinner enquanto a verificação de lote está em andamento.
  buscandoLote?: boolean;
}

// Estilo do destaque da geometria do lote fiscal (verde claro + opacidade — spec).
const estiloLote = () => ({
  color: "#38A169", // green.500
  weight: 2,
  fillColor: "#68D391", // green.300
  fillOpacity: 0.35
});

export const InputMap: React.FC<Props> = ({
  name,
  label,
  error,
  control,
  isAtributos,
  onPontoSelecionado,
  geometria,
  buscandoLote
}) => {
  const [coordinates, setCoordinates] = React.useState<Coord | null>(null);
  const dispatch = useAppDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isMaxOpen,
    onOpen: onMaxOpen,
    onClose: onMaxClose
  } = useDisclosure();

  const { successPayload: consultaPrevia } = useTypedSelector(
    (state) => state.consultaprevia.obterPlanoDiretorNoPonto
  );

  // Mantém o comportamento atual: ao marcar o ponto, pergunta ao plano diretor
  // o que vale naquela coordenada.
  useEffect(() => {
    if (coordinates) {
      dispatch(
        obterPlanoDiretorNoPonto({
          latitude: coordinates.lat,
          longitude: coordinates.lng
        })
      );
    }
  }, [coordinates]);

  const handleSelecionar = (coord: Coord, onChange: (v: Coord) => void) => {
    onChange(coord);
    setCoordinates(coord);
    // D1: dispara a verificação de lote fiscal no formulário.
    onPontoSelecionado?.(coord);
  };

  return (
    <FormControl isInvalid={!!error}>
      {!!label && (
        <Flex justifyContent="space-between" alignItems="center" mb={2}>
          <FormLabel htmlFor={name} mb={0}>
            <Flex alignItems="center">{label}</Flex>
          </FormLabel>
          <HStack spacing={2}>
            {!!isAtributos && (
              <Button
                aria-label="Ver atributos normativos"
                size="xs"
                leftIcon={<RiMenu2Fill />}
                onClick={onOpen}
                isDisabled={!coordinates || !consultaPrevia}
              >
                Consulta prévia
              </Button>
            )}
            <Tooltip label="Maximizar mapa" hasArrow>
              <IconButton
                aria-label="Maximizar mapa"
                size="xs"
                variant="outline"
                icon={<RiFullscreenLine />}
                onClick={onMaxOpen}
              />
            </Tooltip>
          </HStack>
        </Flex>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange } }) => {
          const coord: Coord | null =
            value && "lat" in value ? (value as Coord) : null;
          return (
            <>
              <Box
                position="relative"
                h="64"
                w="full"
                borderRadius="md"
                overflow="hidden"
                border={error ? "2px solid #E53E3E" : "1px solid #E2E8F0"}
              >
                {buscandoLote && (
                  <HStack
                    position="absolute"
                    top={2}
                    left={2}
                    zIndex={1000}
                    bg="white"
                    px={2}
                    py={1}
                    borderRadius="md"
                    boxShadow="sm"
                    spacing={2}
                  >
                    <Spinner size="xs" />
                    <Text fontSize="xs">Verificando lote fiscal…</Text>
                  </HStack>
                )}
                <MapaInterativo
                  value={coord}
                  geometria={geometria}
                  onSelect={(c) => handleSelecionar(c, onChange)}
                />
              </Box>
              {coord && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Ponto selecionado — latitude {coord.lat.toFixed(6)}, longitude{" "}
                  {coord.lng.toFixed(6)}
                </Text>
              )}

              <Modal isOpen={isMaxOpen} onClose={onMaxClose} size="full">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>{label || "Mapa"}</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody pb={6}>
                    <Box h="80vh" w="full" borderRadius="md" overflow="hidden">
                      {isMaxOpen && (
                        <MapaInterativo
                          value={coord}
                          geometria={geometria}
                          onSelect={(c) => handleSelecionar(c, onChange)}
                        />
                      )}
                    </Box>
                    {coord && (
                      <Text fontSize="sm" color="gray.500" mt={2}>
                        Ponto selecionado — latitude {coord.lat.toFixed(6)},
                        longitude {coord.lng.toFixed(6)}
                      </Text>
                    )}
                  </ModalBody>
                </ModalContent>
              </Modal>
            </>
          );
        }}
      />
      {!!error && (
        <FormErrorMessage mt="0.5" fontSize="xs">
          {error.message as string}
        </FormErrorMessage>
      )}
      <ModalConsultaPrevia isOpen={isOpen} onClose={onClose} />
    </FormControl>
  );
};

// Corpo do mapa reaproveitado inline e no modal maximizado. Suporta: clique para
// marcar, arraste do PIN, e destaque da geometria do lote fiscal (GeoJSON).
const MapaInterativo = ({
  value,
  geometria,
  onSelect
}: {
  value: Coord | null;
  geometria?: GeometriaLote;
  onSelect: (c: Coord) => void;
}) => {
  return (
    <MapContainer
      style={{ height: "100%", width: "100%" }}
      zoomControl={false}
      preferCanvas={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <BoundRedirector />
      <AjustarVistaNoResize hasPoint={!!value} />
      {geometria && (
        <>
          <GeoJSON
            key={
              (geometria as any)?.bbox
                ? (geometria as any).bbox.join(",")
                : "geo"
            }
            data={geometria as any}
            style={estiloLote as any}
          />
          <FitGeometria geometria={geometria} />
        </>
      )}
      {value && (
        <Marker
          draggable
          position={{ lat: value.lat, lng: value.lng }}
          eventHandlers={{
            dragend: (e: any) => {
              const { lat, lng } = e.target.getLatLng();
              onSelect({ lat, lng });
            }
          }}
        />
      )}
      <ClickHandler onClick={onSelect} />
    </MapContainer>
  );
};

// Enquadra o mapa na geometria do lote quando ela é identificada, mantendo o PIN.
const FitGeometria = ({ geometria }: { geometria: GeometriaLote }) => {
  const map = useMap();
  useEffect(() => {
    if (!geometria) return;
    try {
      const bounds = L.geoJSON(geometria as any).getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [24, 24], maxZoom: 18 });
      }
    } catch {
      /* geometria inválida: ignora o enquadramento */
    }
  }, [geometria, map]);
  return null;
};

// Leaflet dentro de container que muda de tamanho (accordion/modal) precisa
// recalcular o tamanho E reaplicar o enquadramento. O mapa nasce dentro de um
// Accordion (0px no mount) → o fitBounds inicial do BoundRedirector roda com o
// container em 0px e cai no zoom mundial. Aqui refazemos o enquadramento assim
// que o container mede (e a cada resize), SEM resetar a vista depois que o
// usuário já marcou um ponto (aí valem o marker/geometria do lote).
const AjustarVistaNoResize = ({ hasPoint }: { hasPoint: boolean }) => {
  const map = useMap();
  const { successPayload } = useTypedSelector(
    (state) => state.configuracao.obterConfiguracao
  );
  const hasPointRef = React.useRef(hasPoint);
  hasPointRef.current = hasPoint;

  useEffect(() => {
    const aplicar = () => {
      map.invalidateSize();
      if (!hasPointRef.current && successPayload?.bboxLatLng) {
        map.fitBounds(successPayload.bboxLatLng);
      }
    };

    const inicial = setTimeout(aplicar, 200);

    let debounce: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(() => {
      clearTimeout(debounce);
      debounce = setTimeout(aplicar, 120);
    });
    observer.observe(map.getContainer());

    return () => {
      clearTimeout(inicial);
      clearTimeout(debounce);
      observer.disconnect();
    };
  }, [map, successPayload]);
  return null;
};

const ClickHandler = ({ onClick }: { onClick: (c: Coord) => void }) => {
  useMapEvents({
    click: (e) => {
      onClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    }
  });

  return null;
};
